import { useEffect, useMemo, useState } from "react";
import { subscribeFixtures } from "../services/fixtureService";

function friendlyFixtureError(error) {
  const code = String(error?.code || "");
  const message = String(error?.message || "");
  if (code.includes("permission-denied") || message.toLowerCase().includes("insufficient permissions")) {
    return "League fixture access is blocked by Firestore rules. Deploy the included firestore.rules file.";
  }
  return message || "Unable to load League fixtures.";
}

export default function useLeagueFixtures(orgId, seasonId, currentRound = 0) {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(Boolean(orgId));
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orgId || !seasonId) { setFixtures([]); setLoading(false); return undefined; }
    setLoading(true);
    setError("");
    return subscribeFixtures(orgId, seasonId, (value) => {
      setFixtures(value);
      setLoading(false);
    }, (err) => {
      setError(friendlyFixtureError(err));
      setLoading(false);
    });
  }, [orgId, seasonId]);

  const currentFixtures = useMemo(() => fixtures.filter((fixture) => Number(fixture.round) === Number(currentRound || 1)), [fixtures, currentRound]);
  const upcoming = useMemo(() => fixtures.filter((fixture) => !["complete", "closed"].includes(fixture.status)).slice(0, 6), [fixtures]);
  const recent = useMemo(() => fixtures.filter((fixture) => fixture.status === "complete" || (fixture.status === "closed" && fixture.homeScore != null)).slice(-6).reverse(), [fixtures]);
  const totalRounds = useMemo(() => fixtures.reduce((max, fixture) => Math.max(max, Number(fixture.round || 0)), 0), [fixtures]);
  const completedCount = useMemo(() => fixtures.filter((fixture) => fixture.homeScore != null && fixture.awayScore != null).length, [fixtures]);

  return { fixtures, currentFixtures, upcoming, recent, totalRounds, completedCount, loading, error };
}
